/**
 * Settings Page
 *
 * Full settings page combining profile info, AI configuration (ported from
 * SettingsModal), about / links, and sign-out.  Uses the warm-cream design
 * system (CSS custom properties + editorial typography classes).
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, Check, AlertCircle, Loader2, ExternalLink } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { openRouter } from '@/services/openRouter';
import { DeveloperApiSection } from '@/components/settings/DeveloperApiSection';

// ============================================
// AI CONFIGURATION SECTION
// ============================================

function AIConfigSection() {
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<'valid' | 'invalid' | null>(null);
  const [isSaved, setIsSaved] = useState(false);

  // Load existing key on mount
  useEffect(() => {
    const existingKey = openRouter.getApiKey();
    if (existingKey) {
      setApiKey(existingKey);
      setIsSaved(true);
    }
  }, []);

  const configured = openRouter.isConfigured();

  const handleSave = async () => {
    if (!apiKey.trim()) {
      setValidationResult('invalid');
      return;
    }

    setIsValidating(true);
    setValidationResult(null);

    try {
      const isValid = await openRouter.validateKey(apiKey.trim());
      setValidationResult(isValid ? 'valid' : 'invalid');

      if (isValid) {
        openRouter.setApiKey(apiKey.trim());
        setIsSaved(true);
        // Persist to Drive in background
        openRouter.saveToDrive().catch(() => {
          console.warn('Failed to persist API key to Drive');
        });
      }
    } catch {
      setValidationResult('invalid');
    } finally {
      setIsValidating(false);
    }
  };

  const handleClear = async () => {
    openRouter.clearApiKey();
    await openRouter.clearFromDrive();
    setApiKey('');
    setIsSaved(false);
    setShowKey(false);
    setValidationResult(null);
  };

  return (
    <div className="surface-panel">
      <div className="page-eyebrow mb-3">AI Configuration</div>

      {/* Status indicator */}
      <div className="flex items-center gap-2 mb-4">
        <span
          className="inline-block w-2 h-2 rounded-full"
          style={{ background: configured ? 'var(--accent-primary)' : 'var(--text-tertiary)' }}
        />
        <span className="type-caption" style={{ color: 'var(--text-secondary)' }}>
          {configured ? 'AI assistant configured' : 'Not configured — AI features disabled'}
        </span>
      </div>

      {/* API Key input */}
      <label
        htmlFor="settings-api-key"
        className="type-label block mb-1.5"
        style={{ color: 'var(--text-secondary)' }}
      >
        OpenRouter API Key
      </label>
      <div className="flex flex-col sm:flex-row gap-2 mb-1.5">
        <div className="relative flex-1">
          <input
            id="settings-api-key"
            type={showKey ? 'text' : 'password'}
            value={apiKey}
            onChange={(e) => {
              setApiKey(e.target.value);
              setIsSaved(false);
              setValidationResult(null);
            }}
            placeholder="sk-or-v1-..."
            className="w-full rounded-lg px-3 py-2 pr-9 type-body"
            style={{
              border: '1px solid var(--border-primary)',
              background: 'var(--bg-primary)',
              color: 'var(--text-primary)',
              outline: 'none',
            }}
          />
          <button
            type="button"
            onClick={() => setShowKey((v) => !v)}
            className="absolute right-2 top-1/2 -translate-y-1/2"
            style={{ color: 'var(--text-tertiary)' }}
            aria-label={showKey ? 'Hide API key' : 'Show API key'}
          >
            {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>

        <button
          onClick={() => void handleSave()}
          disabled={isValidating || !apiKey.trim()}
          className="type-label rounded-lg px-4 py-2 transition-opacity"
          style={{
            background: 'var(--accent-primary)',
            color: '#fff',
            opacity: isValidating || !apiKey.trim() ? 0.5 : 1,
            cursor: isValidating || !apiKey.trim() ? 'not-allowed' : 'pointer',
            border: 'none',
          }}
        >
          {isValidating ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : isSaved && validationResult === 'valid' ? (
            <Check className="h-4 w-4" />
          ) : (
            'Save'
          )}
        </button>
      </div>

      {/* Validation feedback */}
      {validationResult === 'valid' && (
        <p className="type-caption flex items-center gap-1" style={{ color: '#16a34a' }}>
          <Check className="h-3.5 w-3.5" />
          API key is valid and saved
        </p>
      )}
      {validationResult === 'invalid' && (
        <p className="type-caption flex items-center gap-1" style={{ color: '#dc2626' }}>
          <AlertCircle className="h-3.5 w-3.5" />
          Invalid API key. Please check and try again.
        </p>
      )}

      {/* Help text */}
      <p className="type-caption mt-2" style={{ color: 'var(--text-tertiary)' }}>
        Get your API key from{' '}
        <a
          href="https://openrouter.ai/keys"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'var(--accent-primary)' }}
          className="inline-flex items-center gap-0.5 hover:underline"
        >
          OpenRouter
          <ExternalLink className="h-3 w-3" />
        </a>
        . Your key is stored in your Google Drive — we never see it.
      </p>

      {/* Current model info */}
      {configured && (
        <div
          className="mt-4 rounded-lg px-3 py-2.5"
          style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)' }}
        >
          <p className="type-label" style={{ color: 'var(--text-secondary)' }}>
            Model:{' '}
            <span style={{ color: 'var(--text-primary)' }}>{openRouter.getModelDisplayName()}</span>
          </p>
          <p className="type-caption mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
            {openRouter.getModel()}
          </p>
        </div>
      )}

      {/* Clear key */}
      {isSaved && (
        <button
          onClick={() => void handleClear()}
          className="type-label mt-4 rounded-lg px-4 py-2 transition-colors"
          style={{
            border: '1px solid var(--border-primary)',
            background: 'transparent',
            color: '#dc2626',
            cursor: 'pointer',
          }}
        >
          Remove API Key
        </button>
      )}
    </div>
  );
}

// ============================================
// MAIN SETTINGS PAGE
// ============================================

export function SettingsPage() {
  const { user, signOut, isSupabaseReady } = useAuth();

  return (
    <div className="page-shell" style={{ maxWidth: '40rem' }}>
      <div className="page-eyebrow">ACCOUNT</div>
      <h1 className="page-title">Settings</h1>

      {/* ---- Profile ---- */}
      {user && (
        <div className="surface-panel mt-6">
          <div className="page-eyebrow mb-3">Profile</div>
          <div className="flex items-center gap-3">
            {user.picture ? (
              <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
            ) : (
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center type-label"
                style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}
              >
                {(user.name || user.email || 'U').slice(0, 1).toUpperCase()}
              </div>
            )}
            <div>
              <p className="type-title" style={{ color: 'var(--text-primary)', margin: 0 }}>
                {user.name}
              </p>
              <p className="type-caption" style={{ color: 'var(--text-tertiary)', margin: 0 }}>
                {user.email}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ---- AI Configuration ---- */}
      <div className="mt-4">
        <AIConfigSection />
      </div>

      {/* ---- Developer API ---- */}
      <DeveloperApiSection isSupabaseReady={isSupabaseReady} />

      {/* ---- About ---- */}
      <div className="surface-panel mt-4">
        <div className="page-eyebrow mb-3">About</div>
        <p className="type-body" style={{ color: 'var(--text-secondary)', margin: 0 }}>
          Personal Insights v1.0.0 — your private knowledge workspace with an AI-curated news feed.
        </p>
        <div className="flex flex-wrap items-center gap-4 mt-3">
          <Link
            to="/privacy"
            className="type-label inline-flex items-center gap-1 hover:underline"
            style={{ color: 'var(--accent-primary)' }}
          >
            Privacy Policy
            <ExternalLink className="h-3 w-3" />
          </Link>
          <a
            href="mailto:hello@insight-os.ai"
            className="type-label inline-flex items-center gap-1 hover:underline"
            style={{ color: 'var(--accent-primary)' }}
          >
            Contact Support
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </div>

      {/* ---- Danger Zone ---- */}
      <div className="surface-panel mt-4">
        <div className="page-eyebrow mb-3" style={{ color: '#dc2626' }}>
          Danger Zone
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p
            className="type-caption"
            style={{ color: 'var(--text-tertiary)', margin: 0, maxWidth: '24rem' }}
          >
            Sign out on this device. Your saved items stay safely in Google Drive.
          </p>
          <button
            onClick={() => signOut()}
            className="type-label rounded-lg px-4 py-2 transition-colors"
            style={{
              border: '1px solid #fca5a5',
              background: '#fef2f2',
              color: '#dc2626',
              cursor: 'pointer',
            }}
          >
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
}
